import { GitMerge, Target, BarChart3, Users, Layout, Zap, TrendingUp } from 'lucide-react';
import KeywordLandingPage, {
  type ProblemCard,
  type FeatureCard,
} from '../components/KeywordLandingPage';

const problemCards: ProblemCard[] = [
  {
    title: 'Teams interpret priorities differently',
    description:
      'Separate plans can leave people with different views of what matters most to the company.',
    icon: GitMerge,
    iconBgClass: 'bg-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'The company goal feels distant',
    description:
      'A high-level objective gives little guidance when people cannot see the projects and tasks connected to it.',
    icon: Target,
    iconBgClass: 'bg-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'Ownership is difficult to follow',
    description:
      'Responsibility gets blurred when the organisation chart and the delivery plan are managed separately.',
    icon: BarChart3,
    iconBgClass: 'bg-yellow-100',
    iconColorClass: 'text-yellow-600',
  },
];

const features: FeatureCard[] = [
  {
    title: 'Visual organisation chart',
    description: 'Map divisions, teams, positions, and people in one organisational view.',
    icon: Users,
    gradientFrom: 'from-blue-50',
    gradientTo: 'to-blue-100',
    iconColorClass: 'text-blue-600',
  },
  {
    title: 'Connected objectives',
    description:
      'Trace company objectives through strategies to the projects and tasks that support them.',
    icon: GitMerge,
    gradientFrom: 'from-purple-50',
    gradientTo: 'to-purple-100',
    iconColorClass: 'text-purple-600',
  },
  {
    title: 'Team access',
    description: 'Invite people to your organisation and manage access with custom roles.',
    icon: Layout,
    gradientFrom: 'from-green-50',
    gradientTo: 'to-green-100',
    iconColorClass: 'text-green-600',
  },
  {
    title: 'Shared strategic foundation',
    description:
      'Keep your vision, mission, values, and objectives together as a reference for decisions.',
    icon: Target,
    gradientFrom: 'from-orange-50',
    gradientTo: 'to-orange-100',
    iconColorClass: 'text-orange-600',
  },
  {
    title: 'AI planning assistance',
    description: 'Draft objectives and strategies using the company context you provide.',
    icon: Zap,
    gradientFrom: 'from-red-50',
    gradientTo: 'to-red-100',
    iconColorClass: 'text-red-600',
  },
  {
    title: 'Progress in context',
    description: 'See work status and progress alongside the strategic hierarchy.',
    icon: TrendingUp,
    gradientFrom: 'from-indigo-50',
    gradientTo: 'to-indigo-100',
    iconColorClass: 'text-indigo-600',
  },
];

export default function StrategicAlignmentSoftwarePage() {
  return (
    <KeywordLandingPage
      headline="Give every team a clear place in the strategy."
      subheadline="Make company objectives visible, connect them to projects and tasks, and show who owns the work across your organisation."
      bullets={[
        'A shared company direction',
        'Objectives connected to daily work',
        'Visual organisation and ownership',
        'Team invitations and custom roles',
      ]}
      badgeText="Strategic Alignment"
      problemTitle="People need to see how their work fits."
      problemSubtitle="A shared strategy becomes easier to act on when the priorities, responsibilities, and connected work are visible together."
      problemCards={problemCards}
      solutionTitle="Connect company priorities to people and work."
      solutionSubtitle="StratPlan combines a shared foundation, a visible work hierarchy, and your organisation structure to make strategic responsibility easier to understand."
      solutionSteps={[
        {
          title: 'Make the direction explicit',
          description: 'Define the vision and measurable objectives your teams are working toward.',
        },
        {
          title: 'Map responsibility',
          description:
            'Build divisions, teams, and positions in your org chart, then assign owners to the work.',
        },
        {
          title: 'Show the connection',
          description:
            'Link strategies, projects, and tasks so people can trace an action back to the objective it supports.',
        },
      ]}
      featuresTitle="Make the connection from company to contribution visible"
      features={features}
      finalCtaTitle="Make the strategy easier for your team to act on."
      finalCtaSubtitle="Create your account and start connecting company priorities to clear responsibilities and next steps."
      finalCtaButtonText="Start my strategic plan"
      analyticsCategory="strategic_alignment_landing"
      breadcrumbLabel="Strategic Alignment"
    />
  );
}
